// src/pages/Servicios.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';
import GlassCard from '../components/UI/GlassCard';
import Modal from '../components/UI/Modal';
import { 
  Scissors, 
  Plus, 
  Edit2, 
  Trash2, 
  Star, 
  Clock, 
  DollarSign, 
  AlertCircle,
  Tag,
  ShieldCheck,
  Search,
  FileText,
  BarChart3,
  PieChart as PieIcon,
  Layers,
  Zap,
  ArrowUpRight
} from 'lucide-react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  Legend 
} from 'recharts';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';

// Componente para evitar el warning de ref en React 19
const SafeTippy = ({ children, ...props }) => {
  const [ref, setRef] = useState(null);
  return (
    <>
      <Tippy {...props} reference={ref} />
      {React.cloneElement(React.Children.only(children), { ref: setRef })}
    </>
  );
};



const Servicios = () => {
    const { can } = useAuth();
    const [servicios, setServicios] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Filtros y Paginación
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategoria, setFilterCategoria] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    // Modales
    const [showModal, setShowModal] = useState(false);
    
    // Form State
    const [editingServicio, setEditingServicio] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        precio_base: '',
        duracion_minutos: '60',
        categoria_id: ''
    });

    const fetchData = useCallback(async () => {
        try {
            const [sRes, cRes] = await Promise.all([
                axios.get('/backend/api/servicios/index.php'),
                axios.get('/backend/api/servicios/categorias.php')
            ]);

            if (sRes.data.success) setServicios(sRes.data.data);
            if (cRes.data.success) setCategorias(cRes.data.data);
        } catch (error) {
            toast.error('Error al cargar catálogo');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Lógica de filtrado
    const filteredServicios = servicios.filter(s => {
        const matchesSearch = s.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             s.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategoria === '' || s.categoria_id.toString() === filterCategoria.toString();
        return matchesSearch && matchesCategory;
    });

    // Lógica de paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredServicios.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredServicios.length / itemsPerPage);

    const handleEdit = (s) => {
        setEditingServicio(s);
        setFormData({
            nombre: s.nombre,
            descripcion: s.descripcion || '',
            precio_base: s.precio_base,
            duracion_minutos: s.duracion_minutos,
            categoria_id: s.categoria_id
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Eliminar Servicio?',
            text: "Esta acción retirará el servicio del catálogo público.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'Sí, retirar',
            background: 'rgba(15, 23, 42, 0.95)',
            color: '#fff'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.delete(`/backend/api/servicios/index.php?id=${id}`);
                if (response.data.success) {
                    toast.success('Catálogo actualizado');
                    fetchData();
                }
            } catch (error) {
                toast.error('Error al procesar baja');
            }
        }
    };

    const handleSave = async () => {
        if (!formData.nombre || !formData.precio_base || !formData.categoria_id) {
            return toast.error('Complete los datos esenciales del servicio');
        }

        try {
            const url = '/backend/api/servicios/index.php';
            const method = editingServicio ? 'PUT' : 'POST';
            const data = editingServicio ? { ...formData, id: editingServicio.id } : formData;

            const response = await axios({ method, url, data });

            if (response.data.success) {
                toast.success(editingServicio ? 'Servicio optimizado' : 'Nuevo servicio lanzado');
                setShowModal(false);
                setEditingServicio(null);
                fetchData();
            }
        } catch (error) {
            toast.error('Error en el despliegue del servicio');
        }
    };

    // 📊 Métricas de Inteligencia de Negocio
    const totalServicios = servicios.length;
    const precioPromedio = totalServicios > 0 ? servicios.reduce((acc, s) => acc + parseFloat(s.precio_base), 0) / totalServicios : 0;
    const tiempoPromedio = totalServicios > 0 ? servicios.reduce((acc, s) => acc + parseInt(s.duracion_minutos), 0) / totalServicios : 0;
    
    // Preparar data para Gráfica de Categorías
    const categoriaData = categorias.map(cat => ({
        name: cat.nombre,
        count: servicios.filter(s => s.categoria_id === cat.id).length
    })).filter(c => c.count > 0);

    const COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ec4899', '#6366f1'];

    if (loading) return (
        <div className="h-screen w-full flex items-center justify-center p-10">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
    );

    return (
        <div className="page-enter p-8 lg:p-12 max-w-[1600px] mx-auto min-h-screen">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
                <div>
                    <h1 className="text-4xl lg:text-5xl font-black flex items-center gap-4 text-white">
                        <Scissors className="text-secondary" size={42} /> 
                        Catálogo de <span className="text-secondary italic font-medium">Servicios</span>
                    </h1>
                    <p className="text-white/40 mt-2 text-sm font-medium flex items-center gap-2">
                        <Zap size={14} className="text-amber-400" />
                        Gestión estratégica de tratamientos y tiempos operativos
                    </p>
                </div>
                
                <div className="flex w-full md:w-auto gap-4 items-center">
                    <SafeTippy content="Descargar catálogo maestro en formato PDF premium" theme="glass-premium">
                        <button 
                            onClick={() => {
                                const token = localStorage.getItem('token');
                                window.open(`/backend/api/reportes/servicios_pdf.php?token=${token}`, '_blank');
                            }}
                            className="glass px-6 h-[52px] rounded-2xl flex items-center gap-3 text-white hover:bg-white/10 transition-all border border-white/10 active:scale-95 group shadow-xl"
                        >
                            <FileText size={20} className="text-secondary group-hover:scale-110 transition-transform" />
                            <span className="text-[14px] font-bold">CATÁLOGO PDF</span>
                        </button>
                    </SafeTippy>

                    {can('Servicios', 'crear') && (
                        <button 
                          onClick={() => { setEditingServicio(null); setShowModal(true); }}
                          className="premium-btn-primary flex items-center gap-3 px-8 h-[52px] group shadow-xl shadow-primary/20 shrink-0"
                        >
                            <Plus size={22} className="group-hover:rotate-180 transition-transform duration-500" />
                            <span>Añadir Servicio</span>
                        </button>
                    )}
                </div>
            </header>

            {/* 📊 KPI DASHBOARD: SERVICIOS */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <GlassCard className="border-white/5 relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 w-24 h-24 bg-secondary/10 rounded-full blur-2xl group-hover:bg-secondary/20 transition-all"></div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-secondary/10 text-secondary">
                            <Layers size={24} />
                        </div>
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-white/40">Total Servicios</span>
                            <div className="text-2xl font-black text-white">{totalServicios}</div>
                        </div>
                    </div>
                </GlassCard>

                <GlassCard className="border-white/5 relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/20 transition-all"></div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-400">
                            <DollarSign size={24} />
                        </div>
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-white/40">Precio Promedio</span>
                            <div className="text-2xl font-black text-white">${precioPromedio.toFixed(2)}</div>
                        </div>
                    </div>
                </GlassCard>

                <GlassCard className="border-white/5 relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl group-hover:bg-amber-500/20 transition-all"></div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-amber-500/10 text-amber-400">
                            <Clock size={24} />
                        </div>
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-white/40">Tiempo Medio</span>
                            <div className="text-2xl font-black text-white">{Math.round(tiempoPromedio)} <span className="text-xs font-medium text-white/20">min</span></div>
                        </div>
                    </div>
                </GlassCard>

                <GlassCard className="border-white/5 relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 w-24 h-24 bg-primary/10 rounded-full blur-2xl group-hover:bg-primary/20 transition-all"></div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-primary/10 text-primary-light">
                            <Star size={24} />
                        </div>
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-white/40">Especialidades</span>
                            <div className="text-2xl font-black text-white">{categorias.length} <span className="text-xs font-medium text-white/20">cats</span></div>
                        </div>
                    </div>
                </GlassCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-4">
                {/* 📉 CHART & FILTERS SECTION */}
                <div className="lg:col-span-1 space-y-6">
                    <GlassCard className="border-white/5" title="Distribución por Categoría">
                        <div className="h-[250px] mt-4">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={categoriaData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="count"
                                    >
                                        {categoriaData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip 
                                        contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
                                        itemStyle={{ color: '#fff' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="space-y-2 mt-4">
                            {categoriaData.map((c, i) => (
                                <div key={i} className="flex items-center justify-between text-[11px] font-bold">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full" style={{backgroundColor: COLORS[i % COLORS.length]}}></div>
                                        <span className="text-white/60">{c.name}</span>
                                    </div>
                                    <span className="text-white">{c.count}</span>
                                </div>
                            ))}
                        </div>
                    </GlassCard>

                    <GlassCard title="Búsqueda Avanzada" className="border-white/5">
                        <div className="space-y-4">
                            <div className="relative">
                                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" />
                                <input 
                                    type="text" 
                                    placeholder="Nombre..."
                                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-11 pr-4 text-white text-sm outline-none"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <select 
                                className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white/70 text-sm outline-none cursor-pointer"
                                value={filterCategoria}
                                onChange={(e) => setFilterCategoria(e.target.value)}
                            >
                                <option value="">Todas las Categorías</option>
                                {categorias.map(cat => (
                                    <option key={cat.id} value={cat.id} className="bg-slate-900">{cat.nombre}</option>
                                ))}
                            </select>
                        </div>
                    </GlassCard>
                </div>

                {/* 📋 SERVICES GRID */}
                <div className="lg:col-span-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {currentItems.map(s => (
                  <GlassCard key={s.id} className="group hover:border-secondary/30 transition-all duration-500 hover:shadow-2xl hover:shadow-secondary/5 relative overflow-hidden" padding="30px">
                    
                    <div className="flex justify-between items-start mb-6 relative">
                       <div className="bg-primary/10 p-3 rounded-2xl border border-primary/20 text-primary-light group-hover:scale-110 transition-transform duration-500">
                          <Star size={24} />
                       </div>
                       <div className="flex flex-col items-end">
                          <span className="text-[10px] font-black uppercase tracking-widest text-primary-light/50 bg-white/5 px-3 py-1 rounded-full mb-2">
                            {s.categoria_nombre}
                          </span>
                          <div className="flex items-center gap-2 text-white/40">
                             <Clock size={12} />
                             <span className="text-xs font-bold">{s.duracion_minutos} min</span>
                          </div>
                       </div>
                    </div>

                    <h3 className="text-xl font-bold text-white/90 mb-2 truncate">{s.nombre}</h3>
                    <p className="text-sm text-white/40 mb-8 line-clamp-2 h-10 font-medium leading-relaxed">
                       {s.descripcion || 'Sin descripción detallada para este servicio excepcional.'}
                    </p>

                    <div className="flex items-center justify-between pt-6 border-t border-white/5 relative">
                       <div className="flex flex-col">
                          <span className="text-[10px] font-black uppercase tracking-widest text-white/20">Precio Base</span>
                          <span className="text-3xl font-black text-white">{formatCurrency(s.precio_base)}</span>
                       </div>
                       
                       <div className="flex gap-2">
                          {can('Servicios', 'editar') && (
                             <SafeTippy content="Editar Servicio">
                                <button onClick={() => handleEdit(s)} className="glass hover:bg-white/10 p-2.5 rounded-xl text-primary-light transition-all active:scale-90">
                                  <Edit2 size={16} />
                                </button>
                             </SafeTippy>
                          )}
                          {can('Servicios', 'eliminar') && (
                             <SafeTippy content="Eliminar del Catálogo">
                                <button onClick={() => handleDelete(s.id)} className="glass hover:bg-red-500/10 p-2.5 rounded-xl text-red-400 transition-all active:scale-90">
                                  <Trash2 size={16} />
                                </button>
                             </SafeTippy>
                          )}
                       </div>
                    </div>
                  </GlassCard>
                ))}

                {filteredServicios.length === 0 && !loading && (
                  <div className="md:col-span-3 py-20 flex flex-col items-center justify-center text-center">
                     <div className="bg-white/5 p-8 rounded-full mb-6 italic border border-white/5">
                        <AlertCircle size={64} className="text-white/10" />
                     </div>
                     <h2 className="text-2xl font-bold text-white/50">No se encontraron servicios</h2>
                     <p className="text-white/20 mt-2 max-w-xs">Intenta ajustar los términos de búsqueda o filtros seleccionados.</p>
                  </div>
                )}
                    </div>
                </div>
            </div>

            {/* PAGINACION */}
            {totalPages > 1 && (
                <div className="mt-12 flex justify-center items-center gap-4">
                    <button 
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => prev - 1)}
                        className="glass px-6 py-2 rounded-xl text-white/50 hover:text-secondary disabled:opacity-20 transition-all"
                    >
                        Anterior
                    </button>
                    <div className="flex gap-2">
                        {[...Array(totalPages)].map((_, i) => (
                            <button 
                                key={i}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`w-10 h-10 rounded-xl font-bold transition-all ${currentPage === i + 1 ? 'bg-secondary text-white shadow-lg shadow-secondary/20' : 'glass text-white/40 hover:bg-white/5'}`}
                            >
                                {i + 1}
                            </button>
                        ))}
                    </div>
                    <button 
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        className="glass px-6 py-2 rounded-xl text-white/50 hover:text-secondary disabled:opacity-20 transition-all"
                    >
                        Siguiente
                    </button>
                </div>
            )}

            {/* Modal Servicio */}
            <Modal 
                show={showModal} 
                onClose={() => setShowModal(false)}
                title={editingServicio ? `Editando: ${formData.nombre}` : 'Diseñar Nuevo Servicio'}
                footer={(
                  <button 
                     onClick={handleSave} 
                     className="premium-btn-primary flex items-center gap-2 px-10 shadow-lg shadow-primary/20"
                  >
                      <ShieldCheck size={18} />
                      {editingServicio ? 'Guardar Cambios' : 'Lanzar Servicio'}
                  </button>
                )}
            >
                <form className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-4">
                   <div className="md:col-span-2 space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light">Título del Servicio</label>
                      <input 
                        type="text" 
                        value={formData.nombre} 
                        onChange={e => setFormData({...formData, nombre: e.target.value})} 
                        className="input-field bg-slate-800/80 text-lg font-bold" 
                        placeholder="Ej. Grooming Completo VIP" 
                      />
                   </div>
                   
                   <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light">Categoría Principal</label>
                      <select 
                        value={formData.categoria_id} 
                        onChange={e => setFormData({...formData, categoria_id: e.target.value})} 
                        className="input-field bg-slate-800/80"
                      >
                          <option value="" disabled>Selecciona categoría...</option>
                          {categorias.map(cat => (
                            <option key={cat.id} value={cat.id} className="bg-slate-900">{cat.nombre}</option>
                          ))}
                      </select>
                   </div>

                   <div className="grid grid-cols-2 gap-4">
                       <div className="space-y-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light">Precio ($)</label>
                          <input type="number" step="0.01" value={formData.precio_base} onChange={e => setFormData({...formData, precio_base: e.target.value})} className="input-field bg-slate-800/80 font-black" placeholder="0.00" />
                       </div>
                       <div className="space-y-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light flex items-center gap-2"><Clock size={10}/> Minutos</label>
                          <input type="number" value={formData.duracion_minutos} onChange={e => setFormData({...formData, duracion_minutos: e.target.value})} className="input-field bg-slate-800/80 font-black" placeholder="60" />
                       </div>
                   </div>

                   <div className="md:col-span-2 space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light flex items-center gap-1">
                         <Tag size={10} /> Descripción del Servicio
                      </label>
                      <textarea 
                        value={formData.descripcion} 
                        onChange={e => setFormData({...formData, descripcion: e.target.value})} 
                        className="input-field h-24 bg-slate-800/80 resize-none font-medium leading-relaxed" 
                        placeholder="Detalla qué incluye este servicio (ej. baño, corte, orejas, uñas...)"
                      ></textarea>
                   </div>
                </form>
            </Modal>
        </div>
    );
};

export default Servicios;
